import React, { useContext } from 'react';
import { Checkbox, Col, Divider, message, Modal, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import useTradeStore from '../../hooks/trade/tradeStore';
import BigNumber from 'bignumber.js';
import { useTradeApi } from '../../hooks/trade/tradeApi'; // ✅ Context 기반 API Hook 사용, Demo/Real 자동 선택
import { useOrderBook } from '../../hooks/trade/orderBook';
import { useTradePosition } from '../../hooks/trade/tradePosition';
import { useTradeSetting } from '../../hooks/trade/tradeSetting';
import { useTradeModalStore } from '../../hooks/trade/modalStore';
import { useIndexPrice } from '../../hooks/trade/indexprice';
import { ThemeContext } from '@/theme/ThemeProvider.tsx';
import { useDisableScroll } from '@utils/useDisableScroll.tsx';
import { formatDecimal } from '@utils/formatDecimal';

import { useBinanceWebSocketStore } from '@app/hooks/binanceWebsocketStore.ts';
import usePercentageStore from '@/hooks/trade/percentageStore';
import { USER_LOG_TYPE, useUserLogger } from '@/app/hooks/userLogger';
import dayjs from 'dayjs';
interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  long_short: string;
}

const CheckPositionModal = ({ open, setOpen, long_short }: IProps) => {
  const { t } = useTranslation();
  const { createOrder } = useTradeApi(); // ✅ Context 기반 API Hook 사용, Demo/Real 자동 선택
  const { data: tradeStore } = useTradeStore();
  const { textw, raise, fail, borderColor, textGrey } = useContext(ThemeContext).theme.COLORS;

  const { getPrice } = useIndexPrice(tradeStore?.ep_id);
  const { data: tradeModalStore, mutate: mutateTradeModalStore } =
    useTradeModalStore();
  const { data: tradeSetting } = useTradeSetting(tradeStore?.ep_id);
  const { mutate: mutateOrderBook } = useOrderBook(tradeStore?.ep_id);
  const { mutateUserLog, getOrderLog } = useUserLogger();  
  const { mutate: mutatePosition } = useTradePosition();
  const main = tradeStore?.pairs?.split('/')[0];
  const currency = tradeStore?.pairs?.split('/')[1];

  //const tradeData = useBinanceWebSocketStore((s) => s.tradeData);
  const globalPercentage = usePercentageStore((state) => state.percentage);

  const sendOrder = async () => {

    if (!tradeStore?.order_amount)
      return message.error(t('pleaseFillAllFields') + '');

    if (tradeStore?.tpsl) {
      if (isNaN(tradeStore?.tp) || isNaN(tradeStore?.sl)) {
        return message.error('CHECK TP/SL');
      }
    }

    // 현재 시장 가격 가져오기
    const currentMarketPrice = getPrice(tradeStore?.ep_id);
    
    // 지정가 주문 여부 확인
    const isLimitOrder = 
      tradeStore?.limit_market?.toUpperCase() == 'L' ||
      tradeStore?.limit_market?.toUpperCase() == 'LIMIT';
    
    // 최종 주문 타입 결정
    let finalOrderType = isLimitOrder ? 'L' : 'M';
    
    if (isLimitOrder) {
      // 주문 가격과 현재 가격 비교
      const orderPrice = Number(tradeStore?.order_price);
      
      // 불리한 가격인지 확인
      const isPriceUnfavorable = 
        (long_short === 'L' && orderPrice > currentMarketPrice) || // 매수인데 지정가가 시장가보다 높음
        (long_short === 'S' && orderPrice < currentMarketPrice);   // 매도인데 지정가가 시장가보다 낮음
      
      // 불리한 가격이면 시장가로 전환
      if (isPriceUnfavorable) {
        finalOrderType = 'M';
        // message.warning('불리한 가격으로 시장가 전환됨');
      }
    }



    const orderParams: any = {
      ep_id: tradeStore?.ep_id,
      main_sub: localStorage.getItem('main_sub') || 'M',
      long_short: long_short,
      cross_iso: tradeSetting?.cross_iso,
      leverage: tradeSetting?.leverage,
      reduce_position: tradeStore.reduce_only ? 1 : 0,
      post_only: tradeStore?.post_only ? 1 : 0,
      limit_market: finalOrderType,
      price: tradeStore?.order_price,
      amount: new BigNumber(tradeStore?.order_amount)
        .dividedBy(tradeSetting?.leverage)
        .toNumber(),
      tpsl: tradeStore?.tpsl,
      tp: tradeStore?.tp,
      sl: tradeStore?.sl,
      tptype: tradeStore?.tptype,
      sltype: tradeStore?.sltype,
      full_gauge: globalPercentage === 100 ? true : false,
    };

    // Time In Force 파라미터 추가 (지정가 주문일 때만)
    if (finalOrderType === 'L') {
      const timeInForce = tradeStore?.time_in_force || 'GTC';
      orderParams.time_in_force = timeInForce;

      if (timeInForce === 'GTD' && tradeStore?.gtd_expiry) {
        orderParams.gtd_expiry = tradeStore.gtd_expiry;
      }
    }

    const result = await createOrder(orderParams); // ✅ Context 기반 API Hook 사용, Demo/Real 자동 선택
    //mutateStore({ ...tradeStore, order_amount: 0 })

    if (result.result.success) {
      mutateUserLog({
        log : getOrderLog(orderParams, USER_LOG_TYPE.ORDER_OPEN),
        data : orderParams,
      })  
      mutateOrderBook();
      mutatePosition();

      setTimeout(() => {
        mutateOrderBook();
        mutatePosition(null);
      }, 500);

      message.success(t('ordersuccess'));
    } else {
      if (result?.result?.message == 'ERROR_MAXAMOUNT') {
        message.error(
          t('ERROR_MAXAMOUNT') +
            ' / MAX AMOUNT : ' +
            BigNumber(result?.result?.max).toFormat(3)
        );
      } else {
        message.error(t(result?.result?.message + ''));
      }
    }
  };

  let margin = new BigNumber(tradeStore?.order_amount)
    .multipliedBy(
      tradeStore?.limit_market == 'M'
        ? getPrice(tradeStore?.ep_id)
        : tradeStore?.order_price
    )
    .dividedBy(tradeSetting?.leverage)
    .toFormat(4);

  if (tradeStore?.main_sub == 'S') {
    margin = new BigNumber(tradeStore?.order_amount)
      .dividedBy(tradeSetting?.leverage)
      .toFormat(3);
  }

  useDisableScroll(open);

  const handleTouchMove = (e) => {
    e.stopPropagation();
  };

  const longShortColor = long_short === 'L' ? raise : fail;
  const longShortText = long_short === 'L' ? t('long') : t('short');

  return (
    <Modal
      className={'custom-modal'}
      open={open}
      title={t('ordercheck') + ''}
      onCancel={() => {
        setOpen(false);
      }}
      onOk={() => {
        sendOrder();
        setOpen(false);
      }}
      cancelText={t('cancel')}
      okText={t('confirm')}
    >
      <div onTouchMove={handleTouchMove}>
        <Divider style={{ margin: '12px 0' }} />

        {/* 거래 코인 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('pairs')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {tradeStore?.pairs}
            </span>
          </Col>
        </Row>

        {/* 가격 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('price')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {tradeStore?.limit_market == 'M'
                ? t('market') + " ("+ formatDecimal(getPrice(tradeStore?.ep_id), tradeSetting?.dp || 2)+")"
                : formatDecimal(tradeStore?.order_price, tradeSetting?.dp || 2)}
            </span>
          </Col>
        </Row>

        {/* 수량 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('amount')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {formatDecimal(tradeStore?.order_amount, 8)}{' '}
              <span style={{ color: textGrey, fontSize: '13px' }}>
                {tradeStore?.main_sub == 'S' ? currency : main}
              </span>
            </span>
          </Col>
        </Row>

        {/* 롱/숏 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('filledType')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{
              fontWeight: 700,
              fontSize: '14px',
              color: longShortColor,
              padding: '4px 12px',
              borderRadius: '4px',
              backgroundColor: `${longShortColor}15`,
              display: 'inline-block'
            }}>
              {longShortText}
            </span>
          </Col>
        </Row>

        {/* 레버리지 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('leverage')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {t(tradeSetting?.cross_iso == 'C' ? 'cross' : 'iso')}{' '}
              <span style={{ color: longShortColor, fontWeight: 700 }}>
                {tradeSetting?.leverage}X
              </span>
            </span>
          </Col>
        </Row>

        {/* 증거금 */}
        <Row style={{
          borderBottom: `1px solid ${borderColor}`,
          padding: '10px 0',
          alignItems: 'center'
        }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>{t('margin')}</span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {margin}{' '}
              <span style={{ color: textGrey, fontSize: '13px' }}>{currency}</span>
            </span>
          </Col>
        </Row>

        {/* 포지션 감소 */}
        {tradeStore?.reduce_only !== undefined && (
          <Row style={{
            borderBottom: `1px solid ${borderColor}`,
            padding: '10px 0',
            alignItems: 'center'
          }}>
            <Col span={8}>
              <span style={{ color: textGrey, fontSize: '13px' }}>
                {t('reduceOnly') || '포지션 감소'}
              </span>
            </Col>
            <Col span={16} style={{ textAlign: 'right' }}>
              <span style={{
                fontWeight: 600,
                fontSize: '14px',
                color: tradeStore?.reduce_only ? raise : textGrey,
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: tradeStore?.reduce_only ? `${raise}15` : 'transparent',
                display: 'inline-block'
              }}>
                {tradeStore?.reduce_only ? '예' : '아니오'}
              </span>
            </Col>
          </Row>
        )}

        {/* Time In Force (지정가 주문일 때만) */}
        {tradeStore?.limit_market === 'L' && (
          <Row style={{
            borderBottom: `1px solid ${borderColor}`,
            padding: '10px 0',
            alignItems: 'center'
          }}>
            <Col span={8}>
              <span style={{ color: textGrey, fontSize: '13px' }}>
                {t('timeInForce') || 'Time In Force'}
              </span>
            </Col>
            <Col span={16} style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
                {tradeStore?.time_in_force === 'GTD'
                  ? `GTD (${dayjs(tradeStore?.gtd_expiry).format('MM/DD HH:mm')})`
                  : 'GTC'
                }
              </span>
            </Col>
          </Row>
        )}

        <Checkbox
          style={{ marginTop: 16, color: textw }}
          checked={tradeModalStore?.skip}
          onChange={(e) => {
            mutateTradeModalStore({
              ...tradeModalStore,
              skip: e.target.checked,
            });
          }}
        >
          {t('notagain')}
        </Checkbox>
      </div>
    </Modal>
  );
};

export default React.memo(CheckPositionModal);
